import { Injectable } from '@angular/core';
import { VerseReference } from '@models/VerseReference';
import { Canon } from "@models/Canon";
import { Verse, VerseResponse } from '@models/Verse';
import { ProjectConfiguration, ProjectId } from '@models/ProjectConfiguration';
import { UserId, UserProfile } from '@models/UserProfile';
import { AdHocPublicationResult, AdHocWorkflowRunsResult, CheckResults } from '@models/database-input-output';
import { CognitoService } from './cognito.service';
import { GreekWordRow } from '@models/GreekWordRow';
import { environment } from 'src/environments/environment';
import { SavedPostRequest } from "@models/SavedPostRequest";
import { HollowPublicationRequest } from '@models/PublicationRequest';
import { HebrewWordRow } from '@models/HebrewWordRow';
import { VerseReferenceJson } from '@models/VerseReferenceJson';
import { UpdateVerseData } from '@models/UpdateVerseData';
import { WrappedBody } from '@models/WrappedBody';
import { GlossSendObject } from '@models/GlossSendObject';
import { Gloss } from '@models/Gloss';
import { UserProfileRow } from '@models/UserProfileRow';
import { ProjectDescription } from '@models/ProjectDescription';
import { UserUpdateObject } from '@models/UserUpdateObject';
import { UbsBook } from '@models/UbsBook';
import { StatsSummary } from "@models/StatsSummary";
import { CommonTasksService } from './common-tasks.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private serverUrl: string = environment.cognito.api_server_url;

  constructor(
    private cognitoService: CognitoService,
    public commonTasks: CommonTasksService,
  ) {
    // Run checkForSavedRequests every 10 seconds
    setInterval(async () => this.checkForSavedRequests(), 10000);

    console.info("Server:", this.serverUrl);
  }

  loadVerseFromAPI(ref: VerseReference, userId: UserId, projectId: ProjectId): Promise<Verse | null> {
    switch (ref.canon) {
      case 'OT':
        return this.getHebrewVerse(ref, userId, projectId);
      case 'NT':
        return this.getNTVerse(ref, userId, projectId);
      case 'LXX':
        console.error("LXX not implemented yet");
        return Promise.reject("LXX not implemented yet");
    }
  }

  private getVerseUrl(ref: VerseReference, projectId: ProjectId): string {
    return `${this.serverUrl}/verse/${projectId}/${ref.toString()}`;
  }

  async getHebrewVerse(ref: VerseReference, userId: UserId, projectId: ProjectId): Promise<Verse> {
    try {
      const url = this.getVerseUrl(ref, projectId);
      const response = await this.authorizedRequest<VerseResponse<HebrewWordRow>>('GET', url);
      return Verse.fromHebrewVerseResponse(ref, response);
    } catch (error) {
      console.error(`Error fetching Hebrew verse: ${error}. Reference: ${ref.toString()}, Project: ${projectId}.`);
      return Promise.reject(`Error fetching Hebrew verse: ${error}. Reference: ${ref.toString()}, Project: ${projectId}.`);
    }
  }

  async getNTVerse(ref: VerseReference, userId: UserId, projectId: ProjectId): Promise<Verse> {
    // try {
    const url = this.getVerseUrl(ref, projectId);
    const response = await this.authorizedRequest<VerseResponse<GreekWordRow>>('GET', url);
    // console.log("getNTVerse", response);
    // console.log(Verse.fromNTVerseResponse(ref, response));
    return Verse.fromNTVerseResponse(ref, response);
    // } catch (error) {
    //   console.error(`Error fetching NT verse: ${error}. Reference: ${ref.toString()}, Project: ${projectId}.`);
    //   return Promise.reject(`Error fetching NT verse: ${error}. Reference: ${ref.toString()}, Project: ${projectId}.`);
    // }
  }

  updateVerse(canon_name: Canon, verse: Verse, projectId: ProjectId): Promise<boolean> {
    const url = `${this.serverUrl}/verse/${projectId}/${verse.reference.toString()}`;
    const glossUpdates = verse.changedGlosses().map(gloss => gloss.toGlossSendObject());
    const phraseGlossUpdates = verse.changedPhraseGlosses().map(gloss => gloss.toGlossSendObject());

    const updateObject: UpdateVerseData = {
      word_gloss_updates: glossUpdates,
      phrase_gloss_updates: phraseGlossUpdates
    };

    // console.log("updateVerse", updateObject);

    verse.markGlossesAsUnchanged();
    return this.safeRequest<boolean, UpdateVerseData>('POST', url, updateObject);
  }

  seekVerse(startingPosition: VerseReference, userId: UserId, projectId: ProjectId, frequencyThreshold: number, direction: "before" | "after", exclusivity: "me" | "anyone"): Promise<VerseReference | undefined> {
    const url = `${this.serverUrl}/verse/${projectId}/${frequencyThreshold}/${startingPosition.toString()}/${direction}/${exclusivity}`;
    return this.authorizedRequest<VerseReferenceJson>('GET', url)
      .then((data) => {
        return VerseReference.fromJson(data);
      });
  }

  saveProjectConfiguration(project: ProjectConfiguration, isNewProject: boolean): Promise<boolean> {
    if (isNewProject) {
      return this.safeRequest('PUT', `${this.serverUrl}/project`, project.toObject());
    } else {
      return this.safeRequest('PUT', `${this.serverUrl}/project/${project.project_id}`, project.toObject());
    }
  }

  getUserData(username: string): Promise<UserProfile> {
    const url = `${this.serverUrl}/user/${username}`;
    return this.authorizedRequest<UserProfileRow>('GET', url)
      .then((data: UserProfileRow) => new UserProfile(data));
  }

  getProjectIdExists(projectId: string): Promise<boolean> {
    const url = `${this.serverUrl}/projectExists/${projectId}`;
    return this.authorizedRequest<boolean>('GET', url);
  }

  updateUser(user: UserUpdateObject): Promise<boolean> {
    const url = `${this.serverUrl}/user/${user.user_id}`;
    return this.safeRequest<boolean, UserUpdateObject>('PUT', url, user);
  }

  private async checkForSavedRequests(): Promise<void> {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("transaction:")) {
        const savedRequest = await SavedPostRequest.fromString(localStorage.getItem(key) || "");
        if (savedRequest) {
          this.retrySavedRequest(savedRequest);
        }
      }
    }
  }

  private async retrySavedRequest(savedRequest: SavedPostRequest): Promise<void> {
    console.log("Found saved request:", savedRequest);
    try {
      await this.authorizedRequest('POST', savedRequest.url, savedRequest);
    } catch (error) {
      /// authorizedRequest will throw an error if the request gives anything but a 200-series response
      /// If the request fails for any reason but authentication, we want to remove it from local storage
      /// so that a bad request doesn't keep getting called.

      /// not great to have no type-checking here.
      const response = error as Response;
      if (response.status !== 401) {
        console.info("Removing saved request from local storage:", savedRequest.hash);
        localStorage.removeItem(savedRequest.hash);
      } else {
        console.error("Request failed due to authentication error. Not removing from local storage.");
        this.cognitoService.ensureStoredAccessTokenIsFresh();
        console.log(this.cognitoService.accessToken$.value);
      }
    }
  }

  private async authorizedRequest<Receive, Send = undefined>(method: 'GET' | 'PUT' | 'POST' | 'DELETE', url: string, body?: Send): Promise<Receive> {
    const id_token = this.cognitoService.accessToken$.value?.accessToken || "";
    if (id_token.length === 0) {
      console.error("Access token was undefined or had zero length.");
      this.commonTasks.showSnackbarMessage("Error. Try reloading the page.", "OK");

      /// we can predict the server's response here, so we can just return a rejected promise
      const response = new Response(null, {
        status: 401,
        statusText: "Unauthorized",
      });
      return Promise.reject(response);
    }

    const request: RequestInit = {
      method,
      headers: {
        'Authorization': id_token,
        'Content-Type': 'application/json',
      },
      ...(body && { body: JSON.stringify(body) }),
    };

    try {
      const serverResponse = await fetch(url, request);
      if (!serverResponse.ok) {
        console.error(`HTTP error! ${method} ${url}`);
        console.error(`HTTP error! Status: ${serverResponse.status}`);
        console.error(`HTTP error! Body: ${await serverResponse.json()}`);

        if (serverResponse.status === 500) {
          this.commonTasks.showSnackbarMessage("Server error. Please try again later.", "OK", 5000);
        } else {
          this.commonTasks.showSnackbarMessage(`Request failed. Please try again. (${serverResponse.status}: ${serverResponse.statusText})`, "OK", 5000);
        }

        return Promise.reject(serverResponse);
      }
      const json = await serverResponse.json();
      return JSON.parse(json) as Receive;
    } catch (error) {
      console.error('Error:', error);
      console.error(url);
      return Promise.reject();
    }
  }

  async safeRequest<Receive, Send = undefined>(method: 'GET' | 'PUT' | 'POST' | 'DELETE', url: string, body?: Send): Promise<Receive> {
    /// save the request to local storage in case it fails
    const savedRequest = await SavedPostRequest.create(url, body);
    localStorage.setItem(savedRequest.hash, savedRequest.toString());
    let wb: WrappedBody<Send> | undefined;
    if (body) {
      wb = { body: body, hash: savedRequest.hash };
    }
    try {
      const result = this.authorizedRequest<Receive, WrappedBody<Send>>(method, url, wb);
      localStorage.removeItem(savedRequest.hash);
      return result;
    } catch (error: unknown) {
      console.log("Error in safeRequest", error);
      return Promise.reject(error);
    }
  }

  getUserIds(): Promise<string[]> {
    const url = `${this.serverUrl}/userids`;
    return this.authorizedRequest<string[]>('GET', url);
  }

  getProjectDescriptions(): Promise<ProjectDescription[]> {
    const url = `${this.serverUrl}/projectdescriptions`;
    return this.authorizedRequest<ProjectDescription[]>('GET', url);
  }

  joinProject(project_id: ProjectId): Promise<boolean> {
    const url = `${this.serverUrl}/joinproject/${project_id}`;
    return this.safeRequest<boolean>('POST', url);
  }

  async performCheck(request: HollowPublicationRequest): Promise<CheckResults> {
    const url = `${this.serverUrl}/check`;
    return this.authorizedRequest<CheckResults, HollowPublicationRequest>('POST', url, request);
  }

  async publish(request: HollowPublicationRequest): Promise<AdHocPublicationResult> {
    const url = `${this.serverUrl}/publish`;
    return this.safeRequest<AdHocPublicationResult, HollowPublicationRequest>('POST', url, request);
  }

  async getActionsForCommit(repo: string, sha: string): Promise<AdHocWorkflowRunsResult> {
    const url = `${this.serverUrl}/action_status/${repo}/${sha}`;
    return await this.authorizedRequest<AdHocWorkflowRunsResult>('GET', url);
  }

  async removeGloss(gloss_id: number): Promise<boolean> {
    if (gloss_id < 0) {
      console.error("Gloss ID cannot be negative:", gloss_id);
      return Promise.reject("Gloss ID cannot be negative");
    }
    // console.log("delete gloss", gloss_id);
    const url = `${this.serverUrl}/gloss/${gloss_id}`;
    return this.safeRequest<boolean>('DELETE', url);
  }

  async editGloss(gloss: Gloss): Promise<boolean> {
    if (gloss.annotation.gloss_id < 0) {
      console.error("Gloss ID cannot be negative:", gloss.gloss_id);
      return Promise.reject("Gloss ID cannot be negative");
    }
    // console.log(gloss.toGlossSendObject());
    const url = `${this.serverUrl}/gloss`;
    return this.safeRequest<boolean, GlossSendObject>('PUT', url, gloss.toGlossSendObject());
  }

  async removePhraseGloss(gloss_id: number): Promise<boolean> {
    if (gloss_id < 0) {
      console.error("Gloss ID cannot be negative:", gloss_id);
      return Promise.reject("Gloss ID cannot be negative");
    }
    // console.log("delete gloss", gloss_id);
    const url = `${this.serverUrl}/phrasegloss/${gloss_id}`;
    return this.safeRequest<boolean>('DELETE', url);
  }

  async editPhraseGloss(gloss: Gloss): Promise<boolean> {
    if (gloss.annotation.gloss_id < 0) {
      console.error("Gloss ID cannot be negative:", gloss.gloss_id);
      return Promise.reject("Gloss ID cannot be negative");
    }
    // console.log(gloss.toGlossSendObject());
    const url = `${this.serverUrl}/phrasegloss`;
    return this.safeRequest<boolean, GlossSendObject>('PUT', url, gloss.toGlossSendObject());
  }

  async getStats(project_id: ProjectId, canon: Canon, book?: UbsBook): Promise<StatsSummary> {
    if (book) {
      const url = `${this.serverUrl}/stats/${project_id}/${canon}/${book}`;
      return this.authorizedRequest<StatsSummary>('GET', url);
    } else {
      const url = `${this.serverUrl}/stats/${project_id}/${canon}`;
      return this.authorizedRequest<StatsSummary>('GET', url);
    }
  }


}

